import React from "react";
import DocumentTitle from "react-document-title";

import categories, { findCategory } from "../../data/faqs/index";
import { getCsWhatsAppUrl } from "../../lib/contact";
import Breadcrumbs from "../../components/Help/Breadcrumbs";
import CategoryRail from "../../components/Help/CategoryRail";
import HelpSearch from "../../components/Help/HelpSearch";
import TopicCard from "../../components/Help/TopicCard";
import NotFound from "../NotFound/NotFound";

/**
 * Splits a category's articles into its sub-topics, in the order
 * `categories.js` lists them. Without sub-topics the category is one card;
 * articles whose `subcategory` is not listed go to a closing "Lainnya" card
 * (the build check rejects unknown values, so that card should stay empty).
 */
function groupBySubtopic(category) {
  const subtopics = category.subtopics || {};
  const keys = Object.keys(subtopics);
  if (keys.length === 0) return [{ key: "all", title: "Semua pertanyaan", description: null, articles: category.articles }];

  const groups = keys.map(key => ({
    key,
    title: subtopics[key].title,
    description: subtopics[key].description,
    articles: category.articles.filter(article => (article.subcategory || keys[0]) === key)
  }));
  const rest = category.articles.filter(article => article.subcategory && keys.indexOf(article.subcategory) === -1);
  if (rest.length > 0) groups.push({ key: "other", title: "Lainnya", description: null, articles: rest });

  return groups.filter(group => group.articles.length > 0);
}

/**
 * A category marked "segera", or one with no articles yet, still opens: the
 * spec forbids dead cards, so it explains itself and offers the chat.
 */
const ComingSoon = ({ category }) => (
  <div className="hc-topic hc-empty">
    <h2 className="hc-topic__head">Artikel {category.title} sedang disiapkan</h2>
    <p className="hc-topic__sub">
      Sementara itu, Team Kyou bisa bantu jawab pertanyaanmu soal {category.title} langsung lewat chat.
    </p>
    <a className="hc-btn" href={getCsWhatsAppUrl(`Halo Kyou, aku mau tanya soal ${category.title}.`)} target="_blank" rel="noopener noreferrer">
      Chat Team Kyou
    </a>
  </div>
);

const Category = ({ match }) => {
  const category = findCategory(match.params.category);
  if (category === null) return <NotFound />;

  const isEmpty = category.badge === "segera" || category.articles.length === 0;
  const count = category.articles.length;

  return (
    <DocumentTitle title={`${category.title} - Kyou Help Center`}>
      <div className="hc-page">
        <header className="hc-cathead">
          <div className="hc-container">
            <Breadcrumbs items={[{ label: "Help Center", to: "/" }, { label: category.title }]} />
            <div className="hc-cathead__row">
              <div>
                <h1 className="hc-cathead__title">{category.title}</h1>
                <p className="hc-cathead__sub">
                  {category.description ? `${category.description} ` : ""}
                  {isEmpty ? "Segera hadir." : `${count} artikel.`}
                </p>
              </div>
              {isEmpty ? null : <HelpSearch variant="compact" category={category} key={category.slug} />}
            </div>
          </div>
        </header>

        <div className="hc-container">
          <div className="hc-split">
            <CategoryRail categories={categories} activeSlug={category.slug} />
            <div className="hc-split__main">
              {isEmpty ? (
                <ComingSoon category={category} />
              ) : (
                groupBySubtopic(category).map(group => (
                  <TopicCard key={group.key} title={group.title} description={group.description} articles={group.articles} />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </DocumentTitle>
  );
};

export default Category;
